import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowRightLeft,
  Building2,
  Check,
  X,
  Clock,
  Loader2,
  User,
  ArrowRight,
} from 'lucide-react';

interface TransferRequest {
  id: string;
  patient_id: string;
  from_agency_id: string;
  to_agency_id: string;
  status: string;
  reason: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  patient: {
    first_name: string;
    last_name: string;
    email: string;
  };
  from_agency: {
    name: string;
    code: string;
  };
  to_agency: {
    name: string;
    code: string;
  };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<any> }> = {
  pending_release: { label: 'Pending Release', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
  released: { label: 'Released', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: ArrowRight },
  pending_acceptance: { label: 'Pending Acceptance', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', icon: Clock },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: Check },
  rejected_by_source: { label: 'Rejected by Source', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: X },
  rejected_by_target: { label: 'Rejected by Target', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: X },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400', icon: X },
};

export function TransferRequestsManager() {
  const { user } = useAdminAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState<TransferRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; request: TransferRequest | null; type: 'source' | 'target' }>({
    open: false,
    request: null,
    type: 'source',
  });
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadRequests();
  }, [user]);

  const loadRequests = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('patient_transfer_requests')
        .select(`
          *,
          patient:patients(first_name, last_name, email),
          from_agency:agencies!patient_transfer_requests_from_agency_id_fkey(name, code),
          to_agency:agencies!patient_transfer_requests_to_agency_id_fkey(name, code)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests((data as unknown as TransferRequest[]) || []);
    } catch (error) {
      console.error('Error loading transfer requests:', error);
      toast({
        title: 'Error',
        description: 'Failed to load transfer requests.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRelease = async (request: TransferRequest) => {
    setProcessingId(request.id);
    try {
      // Update status to pending_acceptance
      const { error: updateError } = await supabase
        .from('patient_transfer_requests')
        .update({
          status: 'pending_acceptance',
          source_approved_at: new Date().toISOString(),
          source_approved_by: user?.id,
        })
        .eq('id', request.id);

      if (updateError) throw updateError;

      // Log the action
      await supabase.from('admin_audit_logs').insert({
        actor_user_id: user?.id,
        action: 'transfer_release_approved',
        target_resource: 'patient_transfer_request',
        target_id: request.id,
        metadata: {
          patient_name: `${request.patient.first_name} ${request.patient.last_name}`,
          from_agency: request.from_agency.name,
          to_agency: request.to_agency.name,
        },
      });

      toast({
        title: 'Release Approved',
        description: `Transfer request sent to ${request.to_agency.name} for acceptance.`,
      });

      loadRequests();
    } catch (error) {
      console.error('Error approving release:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve release. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleAcceptTransfer = async (request: TransferRequest) => {
    setProcessingId(request.id);
    try {
      // Update status to accepted
      const { error: updateError } = await supabase
        .from('patient_transfer_requests')
        .update({
          status: 'accepted',
          target_approved_at: new Date().toISOString(),
          target_approved_by: user?.id,
        })
        .eq('id', request.id);

      if (updateError) throw updateError;

      // Deactivate patient at old agency
      const { error: deactivateError } = await supabase
        .from('patients')
        .update({ is_active: false })
        .eq('id', request.patient_id);

      if (deactivateError) throw deactivateError;

      // Log the action
      await supabase.from('admin_audit_logs').insert({
        actor_user_id: user?.id,
        action: 'transfer_accepted',
        target_resource: 'patient_transfer_request',
        target_id: request.id,
        metadata: {
          patient_name: `${request.patient.first_name} ${request.patient.last_name}`,
          from_agency: request.from_agency.name,
          to_agency: request.to_agency.name,
        },
      });

      toast({
        title: 'Transfer Accepted',
        description: `Patient has been transferred. They can now register with your agency.`,
      });

      loadRequests();
    } catch (error) {
      console.error('Error accepting transfer:', error);
      toast({
        title: 'Error',
        description: 'Failed to accept transfer. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.request) return;

    setProcessingId(rejectDialog.request.id);
    try {
      const newStatus = rejectDialog.type === 'source' ? 'rejected_by_source' : 'rejected_by_target';

      const { error: updateError } = await supabase
        .from('patient_transfer_requests')
        .update({
          status: newStatus,
          rejection_reason: rejectionReason.trim() || null,
        })
        .eq('id', rejectDialog.request.id);

      if (updateError) throw updateError;

      // Log the action
      await supabase.from('admin_audit_logs').insert({
        actor_user_id: user?.id,
        action: `transfer_${rejectDialog.type === 'source' ? 'release' : 'acceptance'}_rejected`,
        target_resource: 'patient_transfer_request',
        target_id: rejectDialog.request.id,
        metadata: {
          patient_name: `${rejectDialog.request.patient.first_name} ${rejectDialog.request.patient.last_name}`,
          rejection_reason: rejectionReason.trim() || 'No reason provided',
        },
      });

      toast({
        title: 'Transfer Rejected',
        description: 'The transfer request has been rejected.',
      });

      setRejectDialog({ open: false, request: null, type: 'source' });
      setRejectionReason('');
      loadRequests();
    } catch (error) {
      console.error('Error rejecting transfer:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject transfer. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const getActionButtons = (request: TransferRequest) => {
    const isFromMyAgency = user?.agency_id === request.from_agency_id;
    const isToMyAgency = user?.agency_id === request.to_agency_id;
    const isSuperAdmin = user?.role === 'super_admin';

    if (request.status === 'pending_release' && (isFromMyAgency || isSuperAdmin)) {
      return (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => handleApproveRelease(request)}
            disabled={processingId === request.id}
          >
            {processingId === request.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                Approve Release
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setRejectDialog({ open: true, request, type: 'source' })}
            disabled={processingId === request.id}
          >
            <X className="h-4 w-4 mr-1" />
            Reject
          </Button>
        </div>
      );
    }

    if (request.status === 'pending_acceptance' && (isToMyAgency || isSuperAdmin)) {
      return (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => handleAcceptTransfer(request)}
            disabled={processingId === request.id}
          >
            {processingId === request.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                Accept Transfer
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setRejectDialog({ open: true, request, type: 'target' })}
            disabled={processingId === request.id}
          >
            <X className="h-4 w-4 mr-1" />
            Reject
          </Button>
        </div>
      );
    }

    return null;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const pendingRequests = requests.filter(r => 
    r.status === 'pending_release' || r.status === 'pending_acceptance'
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Transfer Requests
          </CardTitle>
          <CardDescription>
            Manage patient transfer requests between agencies
          </CardDescription>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-8">
              <ArrowRightLeft className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-medium text-foreground">No transfer requests</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Transfer requests will appear here when patients request to move between agencies.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => {
                const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending_release;
                const StatusIcon = statusConfig.icon;

                return (
                  <div
                    key={request.id}
                    className="border border-border rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {request.patient.first_name[0]}{request.patient.last_name[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="font-medium">
                            {request.patient.first_name} {request.patient.last_name}
                          </h4>
                          <p className="text-sm text-muted-foreground">{request.patient.email}</p>
                        </div>
                      </div>
                      <Badge className={statusConfig.color}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusConfig.label}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <div className="flex items-center gap-1 px-3 py-1.5 bg-muted rounded-lg">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{request.from_agency.name}</span>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <div className="flex items-center gap-1 px-3 py-1.5 bg-muted rounded-lg">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{request.to_agency.name}</span>
                      </div>
                    </div>

                    {request.reason && (
                      <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">
                        <span className="font-medium">Reason:</span> {request.reason}
                      </p>
                    )}

                    {request.rejection_reason && (
                      <p className="text-sm text-destructive bg-destructive/10 rounded-lg p-3">
                        <span className="font-medium">Rejection Reason:</span> {request.rejection_reason}
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-xs text-muted-foreground">
                        Requested {formatDate(request.created_at)}
                      </span>
                      {getActionButtons(request)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rejection Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => {
        if (!open) {
          setRejectDialog({ open: false, request: null, type: 'source' });
          setRejectionReason('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Transfer Request</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this transfer request. This will be shared with the patient.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialog({ open: false, request: null, type: 'source' });
                setRejectionReason('');
              }}
              disabled={processingId !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={processingId !== null}
            >
              {processingId !== null ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rejecting...
                </>
              ) : (
                'Reject Transfer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
